import { useState, useMemo, useEffect } from "react";
import { clamp, isNil, max, min } from "lodash/fp";
import { composeEventHandlers } from "@reach/utils";
import { useHandler } from "./use-handler.es.js";
function useNumberInput(options = {}) {
  const {
    format,
    max: max$1 = Number.MAX_VALUE,
    min: min$1,
    numberType,
    onBlur,
    onChange,
    onFocus,
    step = 1,
    value
  } = options;
  const [rawValue, setRawValue] = useState(
    Number.isFinite(value) ? String(value) : ""
  );
  const [hasFocus, setFocus] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const numberValue = useMemo(() => {
    const parsed = numberType === "integer" ? parseInt(rawValue, 10) : parseFloat(rawValue);
    return Number.isFinite(parsed) ? clamp(min$1, max$1, parsed) : value;
  }, [max$1, min$1, numberType, rawValue, value]);
  const formattedValue = useMemo(() => {
    const val = numberValue || value;
    if (isNil(val)) {
      return "";
    }
    return format ? format(numberValue) : String(numberValue);
  }, [format, numberValue, value]);
  const handleBlur = useHandler(
    composeEventHandlers(onBlur, () => {
      setFocus(false);
    })
  );
  const handleFocus = useHandler(
    composeEventHandlers(onFocus, () => {
      setFocus(true);
    })
  );
  const handleInput = useHandler((val) => {
    setDirty(true);
    setRawValue(val);
  });
  const handleKeyDown = useHandler((event) => {
    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        if (Number.isFinite(numberValue)) {
          setDirty(true);
          setRawValue(String(min([max$1, numberValue + step])));
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (Number.isFinite(numberValue)) {
          setDirty(true);
          setRawValue(String(max([min$1, numberValue - step])));
        }
        break;
    }
  });
  useEffect(() => {
    if (isDirty && value !== numberValue) {
      onChange(numberValue);
    }
    setDirty(false);
  }, [isDirty, numberValue, onChange, value]);
  useEffect(() => {
    if (!hasFocus && Number.isFinite(value)) {
      setRawValue(String(value));
    }
  }, [hasFocus, value]);
  return useMemo(
    () => ({
      inputValue: hasFocus ? rawValue : formattedValue,
      handleBlur,
      handleFocus,
      handleInput,
      handleKeyDown,
      rawValue
    }),
    [
      formattedValue,
      handleBlur,
      handleFocus,
      handleInput,
      handleKeyDown,
      hasFocus,
      rawValue
    ]
  );
}
export {
  useNumberInput
};
